import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Field } from '../../components/ui/Field'
import { Badge } from '../../components/ui/Badge'
import { PermissionGate } from '../../components/PermissionGate'
import { labelsApi } from '../../api/labels.api'
import { usersApi } from '../../api/users.api'
import { rolesApi } from '../../api/roles.api'
import { projectsApi } from '../../api/projects.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import type { ProjectDetail } from '../../api/types'
import formStyles from '../../components/ui/Form.module.css'

export function ProjectSettingsPanel({ project }: { project: ProjectDetail }) {
  const queryClient = useQueryClient()
  const invalidateProject = () => queryClient.invalidateQueries({ queryKey: ['projects', project.id] })

  const [newStatusName, setNewStatusName] = useState('')
  const [newStatusColor, setNewStatusColor] = useState('#6366f1')
  const [newLabelName, setNewLabelName] = useState('')
  const [newLabelColor, setNewLabelColor] = useState('#38bdf8')

  const labelsQuery = useQuery({ queryKey: ['labels', project.id, 'own'], queryFn: async () => (await labelsApi.list(project.id)).data })
  const ownLabels = labelsQuery.data?.filter((l) => l.projectId === project.id) ?? []

  const usersQuery = useQuery({ queryKey: ['users', { pageSize: 100 }], queryFn: async () => (await usersApi.list({ pageSize: 100 })).data })
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: async () => (await rolesApi.list()).data })

  const createStatusMutation = useMutation({
    mutationFn: () => labelsApi.createStatus({ projectId: project.id, name: newStatusName, color: newStatusColor }),
    onSuccess: () => {
      setNewStatusName('')
      invalidateProject()
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Status konnte nicht angelegt werden.'),
  })

  const deleteStatusMutation = useMutation({
    mutationFn: (id: string) => labelsApi.removeStatus(id),
    onSuccess: invalidateProject,
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Status konnte nicht gelöscht werden.'),
  })

  const moveStatusMutation = useMutation({
    mutationFn: (orderedIds: string[]) => labelsApi.reorderStatuses(project.id, orderedIds),
    onSuccess: invalidateProject,
  })

  const createLabelMutation = useMutation({
    mutationFn: () => labelsApi.create({ projectId: project.id, name: newLabelName, color: newLabelColor }),
    onSuccess: () => {
      setNewLabelName('')
      queryClient.invalidateQueries({ queryKey: ['labels'], exact: false })
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Label konnte nicht angelegt werden.'),
  })

  const deleteLabelMutation = useMutation({
    mutationFn: (id: string) => labelsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['labels'], exact: false }),
  })

  const membersMutation = useMutation({
    mutationFn: (userIds: string[]) => projectsApi.setMembers(project.id, userIds),
    onSuccess: invalidateProject,
  })

  const rolesMutation = useMutation({
    mutationFn: (roleIds: string[]) => projectsApi.setRoles(project.id, roleIds),
    onSuccess: invalidateProject,
  })

  const moveStatus = (index: number, direction: -1 | 1) => {
    const ids = project.taskStatuses.map((s) => s.id)
    const target = index + direction
    if (target < 0 || target >= ids.length) return
    ;[ids[index], ids[target]] = [ids[target]!, ids[index]!]
    moveStatusMutation.mutate(ids)
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--space-4)' }}>
      <Card>
        <h3 style={{ marginTop: 0 }}>Status</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 'var(--space-4)' }}>
          {project.taskStatuses.map((status, index) => (
            <div key={status.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: status.color ?? 'var(--text-muted)' }} />
              <span style={{ flex: 1, fontSize: 'var(--text-sm)' }}>{status.name}</span>
              {status.isDone ? <Badge variant="success">Erledigt-Status</Badge> : null}
              <PermissionGate anyOf={['labels.manage']}>
                <Button size="sm" variant="ghost" disabled={index === 0} onClick={() => moveStatus(index, -1)}>
                  ↑
                </Button>
                <Button size="sm" variant="ghost" disabled={index === project.taskStatuses.length - 1} onClick={() => moveStatus(index, 1)}>
                  ↓
                </Button>
                <Button size="sm" variant="danger" onClick={() => deleteStatusMutation.mutate(status.id)}>
                  ✕
                </Button>
              </PermissionGate>
            </div>
          ))}
        </div>
        <PermissionGate anyOf={['labels.manage']}>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Input placeholder="Neuer Status…" value={newStatusName} onChange={(e) => setNewStatusName(e.target.value)} />
            <Input type="color" value={newStatusColor} onChange={(e) => setNewStatusColor(e.target.value)} style={{ width: 44, padding: 4 }} />
            <Button size="sm" disabled={!newStatusName.trim()} loading={createStatusMutation.isPending} onClick={() => createStatusMutation.mutate()}>
              +
            </Button>
          </div>
        </PermissionGate>
      </Card>

      <Card>
        <h3 style={{ marginTop: 0 }}>Labels (projektspezifisch)</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 'var(--space-4)' }}>
          {ownLabels.map((label) => (
            <span key={label.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Badge style={{ background: `${label.color}22`, color: label.color }}>{label.name}</Badge>
              <PermissionGate anyOf={['labels.manage']}>
                <button
                  onClick={() => deleteLabelMutation.mutate(label.id)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 'var(--text-xs)' }}
                >
                  ✕
                </button>
              </PermissionGate>
            </span>
          ))}
          {ownLabels.length === 0 ? <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Keine projektspezifischen Labels.</span> : null}
        </div>
        <PermissionGate anyOf={['labels.manage']}>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Input placeholder="Neues Label…" value={newLabelName} onChange={(e) => setNewLabelName(e.target.value)} />
            <Input type="color" value={newLabelColor} onChange={(e) => setNewLabelColor(e.target.value)} style={{ width: 44, padding: 4 }} />
            <Button size="sm" disabled={!newLabelName.trim()} loading={createLabelMutation.isPending} onClick={() => createLabelMutation.mutate()}>
              +
            </Button>
          </div>
        </PermissionGate>
      </Card>

      <Card>
        <h3 style={{ marginTop: 0 }}>Verantwortliche Mitglieder</h3>
        <Field label="Benutzer">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {usersQuery.data?.map((user) => {
              const checked = project.members.some((m) => m.userId === user.id)
              return (
                <label key={user.id} className={formStyles.checkboxRow} style={{ border: '1px solid var(--surface-border)', borderRadius: 'var(--radius-md)', padding: '4px 10px' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const current = project.members.map((m) => m.userId)
                      const next = checked ? current.filter((id) => id !== user.id) : [...current, user.id]
                      membersMutation.mutate(next)
                    }}
                  />
                  {user.displayName}
                </label>
              )
            })}
          </div>
        </Field>
      </Card>

      <Card>
        <h3 style={{ marginTop: 0 }}>Verantwortliche Rollen</h3>
        <Field label="Rollen">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {rolesQuery.data?.map((role) => {
              const checked = project.roles.some((r) => r.roleId === role.id)
              return (
                <label key={role.id} className={formStyles.checkboxRow} style={{ border: '1px solid var(--surface-border)', borderRadius: 'var(--radius-md)', padding: '4px 10px' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const current = project.roles.map((r) => r.roleId)
                      const next = checked ? current.filter((id) => id !== role.id) : [...current, role.id]
                      rolesMutation.mutate(next)
                    }}
                  />
                  {role.name}
                </label>
              )
            })}
          </div>
        </Field>
      </Card>
    </div>
  )
}
